/*
 * Writing down awkward directions honestly.
 *
 * Each row is one time of day with its own amount, so "ONE in the morning and TWO at night" is
 * two rows. Days of the week, every-so-many-days and date ranges cover weekly medicines, alternate
 * days, twelve-weekly injections and reducing courses.
 *
 * Underneath, the real dates are read back. A pattern can look right and still be wrong; a list of
 * the next few doses cannot hide it.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from './api.ts';

export type Slot = {
  time: string;
  qty?: number | null;
  doseText?: string | null;
  daysOfWeek?: number[] | null;
  everyDays?: number | null;
  anchorOn?: string | null;
  from?: string | null;
  to?: string | null;
  note?: string | null;
};

const DAYS: Array<[number, string]> = [
  [1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun'],
];

export function emptySlot(time = '08:00'): Slot {
  return { time, qty: 1, doseText: '', daysOfWeek: null, everyDays: null, from: null, to: null, note: '' };
}

export function SlotEditor({
  slots,
  onChange,
  startsOn,
  endsOn,
}: {
  slots: Slot[];
  onChange: (slots: Slot[]) => void;
  startsOn?: string;
  endsOn?: string | null;
}) {
  const [preview, setPreview] = useState<Array<{ date: string; time: string; doseText: string }>>([]);
  const [problems, setProblems] = useState<Array<{ slot: number; problem: string }>>([]);
  const [words, setWords] = useState<string[]>([]);

  const update = (index: number, patch: Partial<Slot>) => {
    onChange(slots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));
  };

  const toggleDay = (index: number, day: number) => {
    const current = slots[index]?.daysOfWeek ?? [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort();
    update(index, { daysOfWeek: next.length ? next : null });
  };

  // Read the directions back as real dates, as they are typed.
  const check = useCallback(async () => {
    if (!slots.length) {
      setPreview([]);
      setProblems([]);
      setWords([]);
      return;
    }
    try {
      const data = await api.post<{
        doses: Array<{ date: string; time: string; doseText: string }>;
        problems: Array<{ slot: number; problem: string }>;
        schedule: string[];
      }>('/api/schedule-preview', { slots, startsOn, endsOn: endsOn || null, count: 8 });
      setPreview(data.doses);
      setProblems(data.problems);
      setWords(data.schedule);
    } catch {
      setPreview([]);
    }
  }, [slots, startsOn, endsOn]);

  useEffect(() => {
    const timer = setTimeout(() => void check(), 300);
    return () => clearTimeout(timer);
  }, [check]);

  return (
    <fieldset>
      <legend>When it is due</legend>

      {slots.map((slot, index) => {
        const problem = problems.find((p) => p.slot === index);
        return (
          <div className="slot" key={index}>
            <div className="slot-top">
              <div>
                <label htmlFor={`slot-time-${index}`}>Time</label>
                <input id={`slot-time-${index}`} type="time" required value={slot.time}
                  onChange={(e) => update(index, { time: e.target.value })} />
              </div>
              <div>
                <label htmlFor={`slot-dose-${index}`}>What to give</label>
                <input id={`slot-dose-${index}`} value={slot.doseText ?? ''} placeholder="ONE tablet"
                  onChange={(e) => update(index, { doseText: e.target.value })} />
              </div>
              <div className="narrow">
                <label htmlFor={`slot-qty-${index}`}>How many</label>
                <input id={`slot-qty-${index}`} type="number" inputMode="decimal" step="0.5" min="0"
                  value={slot.qty ?? ''} onChange={(e) => update(index, { qty: e.target.value ? Number(e.target.value) : null })} />
              </div>
              {slots.length > 1 ? (
                <button type="button" className="quiet" onClick={() => onChange(slots.filter((_, i) => i !== index))}>
                  Remove
                </button>
              ) : null}
            </div>

            <div className="days">
              <span className="muted small">Days:</span>
              <button type="button" className={`day ${!slot.daysOfWeek?.length ? 'on' : ''}`}
                onClick={() => update(index, { daysOfWeek: null })}>
                Every day
              </button>
              {DAYS.map(([day, label]) => (
                <button type="button" key={day}
                  className={`day ${slot.daysOfWeek?.includes(day) ? 'on' : ''}`}
                  onClick={() => toggleDay(index, day)}>
                  {label}
                </button>
              ))}
            </div>

            <details>
              <summary>Every so many days, or only between certain dates</summary>
              <div className="slot-extra">
                <div className="narrow">
                  <label htmlFor={`slot-every-${index}`}>Every</label>
                  <input id={`slot-every-${index}`} type="number" min="1" max="365" placeholder="days"
                    value={slot.everyDays ?? ''}
                    onChange={(e) => update(index, { everyDays: e.target.value ? Number(e.target.value) : null })} />
                  <span className="muted small">days. 2 is every other day, 84 is every 12 weeks.</span>
                </div>
                <div>
                  <label htmlFor={`slot-from-${index}`}>From</label>
                  <input id={`slot-from-${index}`} type="date" value={slot.from ?? ''}
                    onChange={(e) => update(index, { from: e.target.value || null })} />
                </div>
                <div>
                  <label htmlFor={`slot-to-${index}`}>Until</label>
                  <input id={`slot-to-${index}`} type="date" value={slot.to ?? ''}
                    onChange={(e) => update(index, { to: e.target.value || null })} />
                </div>
                <div>
                  <label htmlFor={`slot-note-${index}`}>Anything the carer needs to know</label>
                  <input id={`slot-note-${index}`} value={slot.note ?? ''} placeholder="Sitting upright, before food"
                    onChange={(e) => update(index, { note: e.target.value })} />
                </div>
              </div>
            </details>

            {problem ? <p className="error" role="alert">{problem.problem}</p> : null}
          </div>
        );
      })}

      <div className="row-actions">
        <button type="button" className="quiet" onClick={() => onChange([...slots, emptySlot(nextTime(slots))])}>
          Add another time
        </button>
        <button type="button" className="quiet" onClick={() => onChange([...slots, reducingStep(slots)])}>
          Add a step in a reducing course
        </button>
      </div>

      {words.length ? (
        <p className="muted small">{words.join(' · ')}</p>
      ) : null}

      {preview.length ? (
        <div className="preview">
          <strong>The next doses would be</strong>
          <ul>
            {preview.map((dose, index) => (
              <li key={index}>
                {formatDate(dose.date)} at {dose.time} — {dose.doseText}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </fieldset>
  );
}

/* A new step in a reducing course starts the day after the last one ends, at the same time. */
function reducingStep(slots: Slot[]): Slot {
  const last = slots[slots.length - 1];
  const from = last?.to ? addDays(last.to, 1) : null;
  return {
    time: last?.time ?? '08:00',
    qty: last?.qty ?? 1,
    doseText: '',
    daysOfWeek: last?.daysOfWeek ?? null,
    everyDays: null,
    from,
    to: null,
    note: last?.note ?? '',
  };
}

function nextTime(slots: Slot[]): string {
  const used = new Set(slots.map((s) => s.time));
  for (const time of ['08:00', '12:30', '18:00', '20:00', '22:00']) {
    if (!used.has(time)) return time;
  }
  return '08:00';
}

function addDays(date: string, days: number): string {
  const when = new Date(`${date}T12:00:00`);
  when.setDate(when.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
}

function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
